import {AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {environment} from "../../environments/environment";
import {TokenStorageService} from "../_services/token-storage.service";
import {ListingBrowseService} from "../category/listing-browse/listing-browse.service";
import {BreadcrumbService} from "xng-breadcrumb";
import {Globals} from "../globals";
import {Subject, takeUntil} from "rxjs";
import {Router} from "@angular/router";
import {SellerAnalyticsView, SellerListingAnalyticsView, SellerListingView, SellerService} from "../shared/seller.service";
import {FileUploadService} from "../add-item/file-upload.service";
import {ProfileFlashService} from "./profile-flash.service";
import {buildUpdateListingRouterLink} from "../shared/item-url.utils";
import {listingThumbnailUrl} from "../shared/listing-image.utils";
import {PaymentService, ConnectStatus} from "../payment/payment.service";
import {PlanView, SubscriptionService, SubscriptionView} from "../shared/subscription.service";
import {
  BrandTheme,
  BrandThemeService,
  BRAND_FONT_OPTIONS,
  DEFAULT_BRAND_THEME,
  DEFAULT_SHOP_SECTIONS,
  HomePageSection,
  LISTINGS_PER_ROW_OPTIONS,
  SHOP_SECTION_LABELS
} from "../shared/brand-theme.service";
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';

Chart.register(
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

type ProfileTab = 'listings' | 'analytics' | 'dropship' | 'orders' | 'shopfront' | 'about' | 'settings';
type AnalyticsRangeMode = 'preset' | 'custom';

interface AnalyticsSeriesVisibility {
  views: boolean;
  inquiries: boolean;
  favorites: boolean;
  impressions: boolean;
}

interface AnalyticsPrefs {
  mode: AnalyticsRangeMode;
  days: number;
  from: string;
  to: string;
  series: AnalyticsSeriesVisibility;
}

type ListingSortKey = 'name' | 'views' | 'impressions' | 'favorites' | 'inquiries' | 'ctr' | 'conversion';

@Component({
  standalone: false,
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('viewsChartCanvas') viewsChartCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('listingChartCanvas') listingChartCanvas?: ElementRef<HTMLCanvasElement>;

  private readonly analyticsPrefsKey = 'seller_analytics_prefs';

  currentUser: any;
  url: string;
  sellerListings: SellerListingView[] = [];
  analytics: SellerAnalyticsView | null = null;
  analyticsLoading = false;
  analyticsError: string | null = null;
  analyticsRangeMode: AnalyticsRangeMode = 'preset';
  analyticsDays = 30;
  readonly analyticsDayOptions = [7, 30, 90];
  customFrom = '';
  customTo = '';
  customRangeError: string | null = null;
  chartSeries: AnalyticsSeriesVisibility = {
    views: true,
    inquiries: true,
    favorites: true,
    impressions: false
  };
  listingSortKey: ListingSortKey = 'views';
  listingSortAsc = false;
  listingSearch = '';
  selectedListingId: string | null = null;
  listingDetail: SellerListingAnalyticsView | null = null;
  listingDetailLoading = false;
  listingDetailError: string | null = null;
  activeTab: ProfileTab = 'listings';
  userByName : any;
  imageSrc: any;
  listingMessage = '';
  listingError = '';
  resubmittingId: string | null = null;
  connectLoading = false;
  connectMessage = '';
  connectError = '';
  connectStatus: ConnectStatus | null = null;
  connectStatusLoading = false;
  subscription: SubscriptionView | null = null;
  subscriptionPlans: PlanView[] = [];
  subscriptionLoading = false;
  subscriptionError = '';
  subscriptionMessage = '';
  subscriptionCheckoutLoading = false;
  shopTheme: BrandTheme = {...DEFAULT_BRAND_THEME};
  shopThemeUrl = '';
  avatarUrl = '';
  avatarUploading = false;
  avatarError = '';
  shopThemeLoading = false;
  shopThemeSaving = false;
  shopThemeUploading = false;
  shopPromoUploading = false;
  expandedShopSectionId: string | null = 'hero';
  shopThemeMessage = '';
  shopThemeError = '';
  readonly brandFontOptions = BRAND_FONT_OPTIONS;
  readonly listingsPerRowOptions = LISTINGS_PER_ROW_OPTIONS;
  private imagesBaseUrl = '';

  private unsubscribe$ = new Subject<void>();
  private destroyed = false;
  private viewsChart: Chart | null = null;
  private listingChart: Chart | null = null;
  private viewsChartNeedsRender = false;
  private listingChartNeedsRender = false;

  constructor(private token: TokenStorageService,
              private router: Router,
              private profileService: ListingBrowseService,
              private sellerService: SellerService,
              private uploadService: FileUploadService,
              private profileFlash: ProfileFlashService,
              private paymentService: PaymentService,
              private subscriptionService: SubscriptionService,
              private brandThemeService: BrandThemeService,
              private breadcrumbService: BreadcrumbService,
              public globals: Globals) {
    environment.then(environment => {
      this.url = environment["backendURL"];
      this.imageSrc = environment["images"];
      this.imagesBaseUrl = environment["images"] || '';
    });
  }

  ngOnInit(): void {
    this.breadcrumbService.set('@Profile', 'Profile');
    this.currentUser = this.token.getUser();
    this.restoreAnalyticsPrefs();

    if (!this.currentUser) {
      this.router.navigate(['/login']);
      return;
    }

    const flash = this.profileFlash.consumeListingMessage();
    if (flash) {
      this.listingMessage = flash;
    }
    if (this.profileFlash.consumeOpenListingsTab()) {
      this.activeTab = 'listings';
    }

    const params = new URLSearchParams(window.location.search || '');
    const connectParam = params.get('connect');
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (connectParam === 'return' || connectParam === 'refresh') {
      this.activeTab = 'settings';
      this.connectMessage = connectParam === 'return'
        ? 'Returned from Stripe — checking payout status…'
        : 'Stripe onboarding needs another step — continue setup below.';
      window.history.replaceState({}, '', window.location.pathname);
    } else if (tabParam === 'orders' || tabParam === 'dropship' || tabParam === 'settings'
        || tabParam === 'listings' || tabParam === 'analytics' || tabParam === 'shopfront'
        || tabParam === 'about') {
      this.activeTab = tabParam as ProfileTab;
      window.history.replaceState({}, '', window.location.pathname);
    }

    this.loadProfileAndItems();
    this.loadShopTheme();
    if (this.activeTab === 'settings') {
      this.loadSubscription();
      this.loadConnectStatus();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroyViewsChart();
    this.destroyListingChart();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.viewsChartNeedsRender && this.activeTab === 'analytics'
        && this.viewsChartCanvas?.nativeElement && this.analytics?.viewsByDay) {
      this.viewsChartNeedsRender = false;
      this.renderViewsChart();
    }
    if (this.listingChartNeedsRender && this.activeTab === 'analytics'
        && this.listingChartCanvas?.nativeElement && this.listingDetail?.viewsByDay) {
      this.listingChartNeedsRender = false;
      this.renderListingChart();
    }
  }

  private async loadProfileAndItems() {
    if (!this.currentUser) {
      this.router.navigate(['/login']);
      return;
    }
    try {
      // Wait for profile to be loaded
      this.userByName = await this.getProfileByAccountName();
      if (this.destroyed) {
        return;
      }
      this.globals.listingFilter.profile = this.userByName.username || this.currentUser.username;

      this.loadSellerInsights();
    } catch (error) {
      console.error('Error loading profile:', error);
      // Check if error is due to authentication
      if (error.status === 401 || error.status === 403) {
        this.router.navigate(['/login']);
        return;
      }

      if (this.destroyed) {
        return;
      }

      // Fallback to current user
      this.globals.listingFilter.profile = this.currentUser.username;
      this.loadSellerInsights();
    }
  }

  private loadSellerInsights(): void {
    this.reloadAnalytics();
  }

  private currentAnalyticsQuery(): {days?: number; from?: string; to?: string} {
    return this.analyticsRangeMode === 'custom'
      ? {from: this.customFrom, to: this.customTo}
      : {days: this.analyticsDays};
  }

  private reloadAnalytics(): void {
    this.analyticsLoading = true;
    this.analyticsError = null;
    this.customRangeError = null;
    this.destroyViewsChart();
    this.persistAnalyticsPrefs();

    const query = this.currentAnalyticsQuery();

    this.sellerService.getListings(query).subscribe({
      next: listings => {
        this.sellerListings = listings || [];
      },
      error: () => {
        this.sellerListings = [];
      }
    });

    this.sellerService.getAnalytics(query).subscribe({
      next: stats => {
        this.analytics = stats;
        this.analyticsLoading = false;
        if (stats?.fromDate) {
          this.customFrom = stats.fromDate;
        }
        if (stats?.toDate) {
          this.customTo = stats.toDate;
        }
        if (stats?.periodDays && this.analyticsRangeMode === 'preset') {
          this.analyticsDays = stats.periodDays;
        }
        this.viewsChartNeedsRender = this.activeTab === 'analytics';
        if (this.selectedListingId) {
          this.loadListingDetail(this.selectedListingId);
        }
      },
      error: err => {
        this.analyticsLoading = false;
        this.analyticsError = err?.error?.error
          || 'Unable to load analytics. Please try again later.';
        this.destroyViewsChart();
      }
    });
  }

  setAnalyticsDays(days: number): void {
    this.analyticsRangeMode = 'preset';
    this.analyticsDays = days;
    const to = this.todayIso();
    this.customTo = to;
    this.customFrom = this.shiftIso(to, -(days - 1));
    this.reloadAnalytics();
  }

  applyCustomRange(): void {
    if (!this.customFrom || !this.customTo) {
      this.customRangeError = 'Choose both a start and end date.';
      return;
    }
    if (this.customFrom > this.customTo) {
      this.customRangeError = 'Start date must be on or before end date.';
      return;
    }
    const span = this.daySpan(this.customFrom, this.customTo);
    if (span > 180) {
      this.customRangeError = 'Custom range cannot exceed 180 days.';
      return;
    }
    this.analyticsRangeMode = 'custom';
    this.reloadAnalytics();
  }

  toggleChartSeries(key: keyof AnalyticsSeriesVisibility): void {
    this.chartSeries[key] = !this.chartSeries[key];
    this.persistAnalyticsPrefs();
    if (this.activeTab === 'analytics') {
      if (this.analytics?.viewsByDay?.length) {
        this.viewsChartNeedsRender = true;
      }
      if (this.listingDetail?.viewsByDay?.length) {
        this.listingChartNeedsRender = true;
      }
    }
  }

  get periodLabel(): string {
    if (this.analytics?.fromDate && this.analytics?.toDate) {
      return `${this.analytics.fromDate} → ${this.analytics.toDate}`;
    }
    if (this.analyticsRangeMode === 'custom' && this.customFrom && this.customTo) {
      return `${this.customFrom} → ${this.customTo}`;
    }
    return `Last ${this.analyticsDays} days`;
  }

  formatDelta(current?: number | null, previous?: number | null): string {
    const cur = current ?? 0;
    const prev = previous ?? 0;
    if (prev === 0) {
      return cur === 0 ? 'vs prev: —' : 'vs prev: new';
    }
    const pct = ((cur - prev) / prev) * 100;
    const sign = pct > 0 ? '+' : '';
    return `vs prev: ${sign}${pct.toFixed(0)}%`;
  }

  setActiveTab(tab: ProfileTab, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    // Drop focus so :focus / focus rings don't make the control look larger after click
    (event?.currentTarget as HTMLElement | undefined)?.blur();
    this.activeTab = tab;
    if (tab === 'shopfront' || tab === 'settings') {
      this.loadShopTheme();
    }
    if (tab === 'settings') {
      this.loadSubscription();
      this.loadConnectStatus();
    }
    if (tab === 'analytics') {
      if (this.analytics?.viewsByDay) {
        this.viewsChartNeedsRender = true;
      }
      if (this.listingDetail?.viewsByDay) {
        this.listingChartNeedsRender = true;
      }
    }
  }

  get shopThemeFontId(): string {
    return this.brandThemeService.fontOptionId(this.shopTheme?.fontFamily);
  }

  set shopThemeFontId(id: string) {
    this.shopTheme = {
      ...this.shopTheme,
      fontFamily: this.brandThemeService.fontCssForId(id),
    };
  }

  get shopThemeInitial(): string {
    return this.brandThemeService.brandInitial(this.shopTheme?.marketplaceName);
  }

  get shopJumboPreviewUrl(): string {
    return this.brandThemeService.resolveMediaUrl(this.shopTheme?.jumboImageUrl, this.imagesBaseUrl || this.imageSrc);
  }

  get shopPromoPreviewUrl(): string {
    return this.brandThemeService.resolveMediaUrl(this.shopTheme?.promoImageUrl, this.imagesBaseUrl || this.imageSrc);
  }

  get shopPromoBlendPreviewStyle(): {[key: string]: string} {
    return this.brandThemeService.promoBlendStyle(
      this.shopTheme?.promoBlendOpacity, this.shopTheme?.promoBlendFade);
  }

  get shopPromoHeroPreviewBackground(): string {
    const color = (this.shopTheme?.jumboBackgroundColor || '').trim();
    return color || 'linear-gradient(135deg, #ffffff 0%, #f1f4f8 100%)';
  }

  setShopPromoBlendOpacity(value: number | string): void {
    this.shopTheme = {
      ...this.shopTheme,
      promoBlendOpacity: this.brandThemeService.promoBlendOpacityOrDefault(value),
    };
  }

  setShopPromoBlendFade(value: number | string): void {
    this.shopTheme = {
      ...this.shopTheme,
      promoBlendFade: this.brandThemeService.promoBlendFadeOrDefault(value),
    };
  }

  get avatarDisplayUrl(): string {
    return this.brandThemeService.resolveMediaUrl(this.avatarUrl, this.imagesBaseUrl || this.imageSrc)
      || 'assets/images/avatar-placeholder.jpg';
  }

  listingPhotoUrl(photo: string | null | undefined): string {
    return listingThumbnailUrl(this.imagesBaseUrl || this.imageSrc, photo)
      || 'assets/images/cat-2.jpg';
  }

  loadShopTheme(): void {
    if (this.shopThemeLoading) {
      return;
    }
    this.shopThemeLoading = true;
    this.shopThemeError = '';
    this.brandThemeService.fetchSellerTheme().subscribe({
      next: (res) => {
        this.shopThemeLoading = false;
        this.shopTheme = {
          ...res.theme,
          sections: (res.theme.sections || DEFAULT_SHOP_SECTIONS).map(s => ({...s, props: {...(s.props || {})}})),
        };
        this.shopThemeUrl = res.profileUrl || '';
        this.avatarUrl = res.avatarUrl || '';
        if (!this.expandedShopSectionId && this.shopTheme.sections.length) {
          this.expandedShopSectionId = this.shopTheme.sections[0].id;
        }
      },
      error: () => {
        this.shopThemeLoading = false;
        this.shopThemeError = 'Could not load shop theme.';
      }
    });
  }

  saveShopTheme(): void {
    this.shopThemeSaving = true;
    this.shopThemeMessage = '';
    this.shopThemeError = '';
    this.brandThemeService.saveSellerTheme(this.shopTheme).subscribe({
      next: (res) => {
        this.shopThemeSaving = false;
        this.shopTheme = {
          ...res.theme,
          sections: (res.theme.sections || DEFAULT_SHOP_SECTIONS).map(s => ({...s, props: {...(s.props || {})}})),
        };
        this.shopThemeUrl = res.profileUrl || this.shopThemeUrl;
        this.shopThemeMessage = 'Shopfront saved. Open your shop page to preview changes.';
      },
      error: (err) => {
        this.shopThemeSaving = false;
        this.shopThemeError = err?.error?.error || 'Could not save shop theme.';
      }
    });
  }

  resetShopThemeDefaults(): void {
    this.shopTheme = {
      ...DEFAULT_BRAND_THEME,
      marketplaceName: this.shopTheme.marketplaceName || DEFAULT_BRAND_THEME.marketplaceName,
      jumboImageUrl: '',
      jumboBackgroundColor: '',
      heroHighlight: '',
      browseLink: '',
      promoMode: 'none',
      promoImageUrl: '',
      sections: DEFAULT_SHOP_SECTIONS.map(s => ({...s, props: {...s.props}})),
    };
    this.expandedShopSectionId = 'hero';
  }

  onShopSectionDrop(event: CdkDragDrop<HomePageSection[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const sections = [...this.shopTheme.sections];
    moveItemInArray(sections, event.previousIndex, event.currentIndex);
    this.shopTheme = {...this.shopTheme, sections};
  }

  toggleShopSection(section: HomePageSection): void {
    section.enabled = !section.enabled;
  }

  expandShopSection(section: HomePageSection): void {
    this.expandedShopSectionId = this.expandedShopSectionId === section.id ? null : section.id;
  }

  isShopCoreSection(type: string): boolean {
    return type === 'hero' || type === 'latest';
  }

  shopSectionLabel(type: string): string {
    return (SHOP_SECTION_LABELS as Record<string, string>)[type] || type;
  }

  addShopBanner(): void {
    if (this.shopTheme.sections.length >= 12) {
      return;
    }
    const section = this.brandThemeService.createShopSection('banner');
    this.shopTheme = {...this.shopTheme, sections: [...this.shopTheme.sections, section]};
    this.expandedShopSectionId = section.id;
  }

  addShopRichtext(): void {
    if (this.shopTheme.sections.length >= 12) {
      return;
    }
    const section = this.brandThemeService.createShopSection('richtext');
    this.shopTheme = {...this.shopTheme, sections: [...this.shopTheme.sections, section]};
    this.expandedShopSectionId = section.id;
  }

  removeShopSection(section: HomePageSection): void {
    if (this.isShopCoreSection(section.type)) {
      return;
    }
    this.shopTheme = {
      ...this.shopTheme,
      sections: this.shopTheme.sections.filter(s => s.id !== section.id),
    };
    if (this.expandedShopSectionId === section.id) {
      this.expandedShopSectionId = null;
    }
  }

  onShopJumboSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.shopThemeUploading = true;
    this.shopThemeError = '';
    this.brandThemeService.uploadSellerJumboImage(file).subscribe({
      next: url => {
        this.shopThemeUploading = false;
        this.shopTheme = {...this.shopTheme, jumboImageUrl: url};
        this.shopThemeMessage = 'Hero image uploaded. Click Save shopfront to publish.';
      },
      error: err => {
        this.shopThemeUploading = false;
        this.shopThemeError = err?.error?.error || 'Hero image upload failed.';
      }
    });
  }

  clearShopJumbo(): void {
    this.shopTheme = {...this.shopTheme, jumboImageUrl: ''};
  }

  onShopPromoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.shopPromoUploading = true;
    this.shopThemeError = '';
    this.brandThemeService.uploadSellerPromoImage(file).subscribe({
      next: url => {
        this.shopPromoUploading = false;
        this.shopTheme = {...this.shopTheme, promoImageUrl: url, promoMode: 'image'};
        this.shopThemeMessage = 'Promo image uploaded. Click Save shopfront to publish.';
      },
      error: err => {
        this.shopPromoUploading = false;
        this.shopThemeError = err?.error?.error || 'Promo image upload failed.';
      }
    });
  }

  clearShopPromo(): void {
    this.shopTheme = {...this.shopTheme, promoImageUrl: ''};
  }

  onAvatarFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.avatarUploading = true;
    this.avatarError = '';
    this.brandThemeService.uploadSellerAvatar(file).subscribe({
      next: url => {
        this.avatarUploading = false;
        this.avatarUrl = url;
      },
      error: err => {
        this.avatarUploading = false;
        this.avatarError = err?.error?.error || 'Avatar upload failed.';
      }
    });
  }

  clearAvatar(): void {
    this.avatarUploading = true;
    this.avatarError = '';
    this.brandThemeService.clearSellerAvatar().subscribe({
      next: res => {
        this.avatarUploading = false;
        this.avatarUrl = res.avatarUrl || '';
      },
      error: err => {
        this.avatarUploading = false;
        this.avatarError = err?.error?.error || 'Could not remove avatar.';
      }
    });
  }

  startConnectOnboarding(): void {
    this.connectLoading = true;
    this.connectError = '';
    this.connectMessage = '';
    this.paymentService.startConnectOnboarding().subscribe({
      next: (res) => {
        this.connectLoading = false;
        if (res?.url) {
          window.location.href = res.url;
          return;
        }
        this.connectMessage = res?.message || 'Stripe Connect is not configured (mock mode).';
        this.loadConnectStatus();
      },
      error: (err) => {
        this.connectLoading = false;
        this.connectError = err?.error?.error || 'Could not start payouts onboarding.';
      }
    });
  }

  loadConnectStatus(): void {
    this.connectStatusLoading = true;
    this.paymentService.getConnectStatus().subscribe({
      next: (status) => {
        this.connectStatusLoading = false;
        this.connectStatus = status;
      },
      error: () => {
        this.connectStatusLoading = false;
        this.connectStatus = null;
      }
    });
  }

  get connectButtonLabel(): string {
    if (this.connectLoading) {
      return 'Opening…';
    }
    if (this.connectStatus?.connected) {
      return 'Update payouts';
    }
    if (this.connectStatus?.stripeConnectAccountId) {
      return 'Continue setup';
    }
    return 'Set up payouts';
  }

  get connectStatusLabel(): string {
    if (this.connectStatusLoading) {
      return 'Checking Connect status…';
    }
    if (!this.connectStatus) {
      return '';
    }
    if (this.connectStatus.mode === 'mock') {
      return this.connectStatus.message || 'Stripe Connect is in mock mode.';
    }
    if (this.connectStatus.connected) {
      return 'Payouts ready — charges and onboarding are complete.';
    }
    if (this.connectStatus.stripeConnectAccountId) {
      return 'Onboarding incomplete — continue setup to enable payouts.';
    }
    return 'Not connected yet.';
  }

  loadSubscription(): void {
    this.subscriptionLoading = true;
    this.subscriptionError = '';
    this.subscriptionService.getMe().subscribe({
      next: (sub) => {
        this.subscription = sub;
        this.subscriptionLoading = false;
      },
      error: (err) => {
        this.subscriptionLoading = false;
        this.subscriptionError = err?.error?.error || 'Could not load plan usage.';
      }
    });
    this.subscriptionService.listPlans().subscribe({
      next: (plans) => (this.subscriptionPlans = plans || []),
      error: () => (this.subscriptionPlans = [])
    });
  }

  startPlanCheckout(planCode: string): void {
    if (!planCode || this.subscriptionCheckoutLoading) {
      return;
    }
    this.subscriptionCheckoutLoading = true;
    this.subscriptionError = '';
    this.subscriptionMessage = '';
    this.subscriptionService.checkout(planCode).subscribe({
      next: (res) => {
        this.subscriptionCheckoutLoading = false;
        if (res?.checkoutUrl) {
          window.location.href = res.checkoutUrl;
          return;
        }
        this.subscriptionMessage = res?.message || `Plan updated to ${res?.planCode || planCode}.`;
        this.loadSubscription();
      },
      error: (err) => {
        this.subscriptionCheckoutLoading = false;
        this.subscriptionError = err?.error?.error || 'Could not start plan checkout.';
      }
    });
  }

  formatPlanPrice(cents: number): string {
    if (!cents || cents <= 0) {
      return 'Free';
    }
    return `${(cents / 100).toFixed(2)} / mo`;
  }

  get listingUsagePercent(): number {
    const sub = this.subscription;
    if (!sub || !sub.maxListings) {
      return 0;
    }
    return Math.min(100, (sub.activeListings / sub.maxListings) * 100);
  }

  get performanceListings(): SellerListingView[] {
    const rows = this.analytics?.listings?.length
      ? [...this.analytics.listings]
      : [...(this.analytics?.topListings || [])];
    const q = (this.listingSearch || '').trim().toLowerCase();
    const filtered = q
      ? rows.filter(row => (row.name || '').toLowerCase().includes(q)
        || (row.status || '').toLowerCase().includes(q))
      : rows;
    filtered.sort((a, b) => {
      const av = this.sortValue(a, this.listingSortKey);
      const bv = this.sortValue(b, this.listingSortKey);
      if (typeof av === 'string' && typeof bv === 'string') {
        return this.listingSortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number(av);
      const bn = Number(bv);
      return this.listingSortAsc ? an - bn : bn - an;
    });
    return filtered;
  }

  setListingSort(key: ListingSortKey): void {
    if (this.listingSortKey === key) {
      this.listingSortAsc = !this.listingSortAsc;
    } else {
      this.listingSortKey = key;
      this.listingSortAsc = key === 'name';
    }
  }

  openListingBreakdown(row: SellerListingView): void {
    if (!row?.itemId) {
      return;
    }
    this.selectedListingId = row.itemId;
    this.loadListingDetail(row.itemId);
  }

  closeListingBreakdown(): void {
    this.selectedListingId = null;
    this.listingDetail = null;
    this.listingDetailError = null;
    this.listingDetailLoading = false;
    this.destroyListingChart();
  }

  private loadListingDetail(itemId: string): void {
    this.listingDetailLoading = true;
    this.listingDetailError = null;
    this.destroyListingChart();
    this.sellerService.getListingAnalytics(itemId, this.currentAnalyticsQuery()).subscribe({
      next: detail => {
        this.listingDetail = detail;
        this.listingDetailLoading = false;
        this.listingChartNeedsRender = this.activeTab === 'analytics';
      },
      error: err => {
        this.listingDetailLoading = false;
        this.listingDetail = null;
        this.listingDetailError = err?.error?.error || 'Unable to load listing analytics.';
      }
    });
  }

  private sortValue(row: SellerListingView, key: ListingSortKey): string | number {
    switch (key) {
      case 'name':
        return (row.name || '').toLowerCase();
      case 'impressions':
        return row.impressionsLast30Days || 0;
      case 'favorites':
        return row.favoritesLast30Days || 0;
      case 'inquiries':
        return row.inquiriesLast30Days || 0;
      case 'ctr':
        return row.ctrPercent || 0;
      case 'conversion':
        return row.conversionPercent || 0;
      case 'views':
      default:
        return row.viewsLast30Days || 0;
    }
  }

  private renderViewsChart(): void {
    const canvas = this.viewsChartCanvas?.nativeElement;
    const series = this.analytics?.viewsByDay;
    if (!canvas || !series?.length) {
      return;
    }

    this.destroyViewsChart();

    const labels = series.map(d => this.formatChartDate(d.date));
    const step = series.length > 14 ? Math.ceil(series.length / 8) : 1;
    const dense = series.length > 45;
    const datasets = [];

    if (this.chartSeries.views) {
      datasets.push({
        label: 'Views',
        data: series.map(d => d.views ?? 0),
        borderColor: '#d4af37',
        backgroundColor: 'rgba(212, 175, 55, 0.12)',
        borderWidth: 2,
        pointRadius: dense ? 0 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#d4af37',
        tension: 0.3,
        fill: true,
        yAxisID: 'y'
      });
    }
    if (this.chartSeries.inquiries) {
      datasets.push({
        label: 'Inquiries',
        data: series.map(d => d.inquiries ?? 0),
        borderColor: '#2f6fed',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: dense ? 0 : 2,
        pointHoverRadius: 4,
        pointBackgroundColor: '#2f6fed',
        tension: 0.3,
        fill: false,
        yAxisID: 'y'
      });
    }
    if (this.chartSeries.favorites) {
      datasets.push({
        label: 'Favorites',
        data: series.map(d => d.favorites ?? 0),
        borderColor: '#c0392b',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: dense ? 0 : 2,
        pointHoverRadius: 4,
        pointBackgroundColor: '#c0392b',
        tension: 0.3,
        fill: false,
        yAxisID: 'y'
      });
    }
    if (this.chartSeries.impressions) {
      datasets.push({
        label: 'Impressions',
        data: series.map(d => d.impressions ?? 0),
        borderColor: '#6c757d',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: dense ? 0 : 2,
        pointHoverRadius: 4,
        pointBackgroundColor: '#6c757d',
        tension: 0.3,
        fill: false,
        yAxisID: 'y'
      });
    }

    if (!datasets.length) {
      return;
    }

    this.viewsChart = new Chart(canvas, {
      type: 'line',
      data: {labels, datasets},
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {mode: 'index', intersect: false},
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {boxWidth: 12, usePointStyle: true}
          },
          tooltip: {
            callbacks: {
              title: items => {
                const idx = items[0]?.dataIndex ?? 0;
                return series[idx]?.date || '';
              }
            }
          }
        },
        scales: {
          x: {
            grid: {display: false},
            ticks: {
              maxRotation: 0,
              autoSkip: false,
              callback: (_value, index) =>
                index % step === 0 || index === labels.length - 1 ? labels[index] : ''
            }
          },
          y: {
            beginAtZero: true,
            ticks: {precision: 0},
            grid: {color: 'rgba(0, 0, 0, 0.06)'}
          }
        }
      }
    });
  }

  formatRate(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return '0%';
    }
    return `${Number(value).toFixed(1)}%`;
  }

  private restoreAnalyticsPrefs(): void {
    const today = this.todayIso();
    this.customTo = today;
    this.customFrom = this.shiftIso(today, -29);
    try {
      const raw = localStorage.getItem(this.analyticsPrefsKey);
      if (!raw) {
        return;
      }
      const prefs = JSON.parse(raw) as AnalyticsPrefs;
      if (prefs?.days && [7, 30, 90].includes(prefs.days)) {
        this.analyticsDays = prefs.days;
      }
      if (prefs?.mode === 'custom' || prefs?.mode === 'preset') {
        this.analyticsRangeMode = prefs.mode;
      }
      if (prefs?.from && prefs?.to) {
        this.customFrom = prefs.from;
        this.customTo = prefs.to;
      } else if (this.analyticsRangeMode === 'preset') {
        this.customTo = today;
        this.customFrom = this.shiftIso(today, -(this.analyticsDays - 1));
      }
      if (prefs?.series) {
        this.chartSeries = {
          views: prefs.series.views !== false,
          inquiries: prefs.series.inquiries !== false,
          favorites: prefs.series.favorites !== false,
          impressions: !!prefs.series.impressions
        };
      }
    } catch {
      // ignore bad prefs
    }
  }

  private persistAnalyticsPrefs(): void {
    try {
      const prefs: AnalyticsPrefs = {
        mode: this.analyticsRangeMode,
        days: this.analyticsDays,
        from: this.customFrom,
        to: this.customTo,
        series: {...this.chartSeries}
      };
      localStorage.setItem(this.analyticsPrefsKey, JSON.stringify(prefs));
    } catch {
      // ignore
    }
  }

  private todayIso(): string {
    const d = new Date();
    return this.toIso(d);
  }

  private shiftIso(iso: string, dayDelta: number): string {
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + dayDelta);
    return this.toIso(d);
  }

  private toIso(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private daySpan(from: string, to: string): number {
    const a = new Date(`${from}T12:00:00`).getTime();
    const b = new Date(`${to}T12:00:00`).getTime();
    return Math.floor((b - a) / 86400000) + 1;
  }

  private formatChartDate(isoDate: string): string {
    const parts = (isoDate || '').split('-');
    if (parts.length !== 3) {
      return isoDate;
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[Number(parts[1]) - 1] || parts[1];
    return `${month} ${Number(parts[2])}`;
  }

  private destroyViewsChart(): void {
    if (this.viewsChart) {
      this.viewsChart.destroy();
      this.viewsChart = null;
    }
  }

  private renderListingChart(): void {
    const canvas = this.listingChartCanvas?.nativeElement;
    const series = this.listingDetail?.viewsByDay;
    if (!canvas || !series?.length) {
      return;
    }
    this.destroyListingChart();
    const labels = series.map(d => this.formatChartDate(d.date));
    const step = series.length > 14 ? Math.ceil(series.length / 8) : 1;
    const dense = series.length > 45;
    const datasets = [];
    if (this.chartSeries.views) {
      datasets.push({
        label: 'Views',
        data: series.map(d => d.views ?? 0),
        borderColor: '#d4af37',
        backgroundColor: 'rgba(212, 175, 55, 0.12)',
        borderWidth: 2,
        pointRadius: dense ? 0 : 3,
        tension: 0.3,
        fill: true
      });
    }
    if (this.chartSeries.inquiries) {
      datasets.push({
        label: 'Inquiries',
        data: series.map(d => d.inquiries ?? 0),
        borderColor: '#2f6fed',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: dense ? 0 : 2,
        tension: 0.3,
        fill: false
      });
    }
    if (this.chartSeries.favorites) {
      datasets.push({
        label: 'Favorites',
        data: series.map(d => d.favorites ?? 0),
        borderColor: '#c0392b',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: dense ? 0 : 2,
        tension: 0.3,
        fill: false
      });
    }
    if (this.chartSeries.impressions) {
      datasets.push({
        label: 'Impressions',
        data: series.map(d => d.impressions ?? 0),
        borderColor: '#6c757d',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: dense ? 0 : 2,
        tension: 0.3,
        fill: false
      });
    }
    if (!datasets.length) {
      return;
    }
    this.listingChart = new Chart(canvas, {
      type: 'line',
      data: {labels, datasets},
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {mode: 'index', intersect: false},
        plugins: {
          legend: {display: true, position: 'bottom', labels: {boxWidth: 12, usePointStyle: true}},
          tooltip: {
            callbacks: {
              title: items => series[items[0]?.dataIndex ?? 0]?.date || ''
            }
          }
        },
        scales: {
          x: {
            grid: {display: false},
            ticks: {
              maxRotation: 0,
              autoSkip: false,
              callback: (_value, index) =>
                index % step === 0 || index === labels.length - 1 ? labels[index] : ''
            }
          },
          y: {
            beginAtZero: true,
            ticks: {precision: 0},
            grid: {color: 'rgba(0, 0, 0, 0.06)'}
          }
        }
      }
    });
  }

  private destroyListingChart(): void {
    if (this.listingChart) {
      this.listingChart.destroy();
      this.listingChart = null;
    }
  }

  listingStatus(itemId: string): string {
    const row = this.sellerListings.find(l => l.itemId === itemId);
    return row?.status || 'PUBLISHED';
  }

  statusLabel(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'PENDING':
        return 'Awaiting review';
      case 'REJECTED':
        return 'Rejected';
      case 'DRAFT':
        return 'Draft';
      case 'PUBLISHED':
        return 'Live';
      case 'EXPIRED':
        return 'Expired';
      default:
        return status || 'Live';
    }
  }

  canResubmit(item: SellerListingView): boolean {
    const status = (item.status || this.listingStatus(item.itemId) || '').toUpperCase();
    return status === 'REJECTED' || status === 'DRAFT';
  }

  resubmitListing(itemId: string): void {
    if (this.resubmittingId === itemId) {
      return;
    }
    this.listingError = '';
    this.listingMessage = '';
    this.resubmittingId = itemId;
    this.sellerService.resubmit(itemId).subscribe({
      next: () => {
        this.resubmittingId = null;
        this.listingMessage = 'Listing resubmitted for review.';
        this.loadSellerInsights();
      },
      error: err => {
        this.resubmittingId = null;
        this.listingError = err?.error?.message || err?.error?.error || 'Resubmit failed. Please try again.';
      }
    });
  }

  private getProfileByAccountName(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.profileService.getProfileByAccountName(this.url + '/profile/username', this.currentUser.username)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (response) => {
            console.log('userByName', response);
            resolve(response);
          },
          error: (err) => {
            console.error('Error fetching profile:', err);
            reject(err);
          }
        });
    });
  }

  editListing(itemId: string): void {
    this.uploadService.getListingForEdit(this.url, itemId).subscribe({
      next: payload => {
        const item = payload?.item;
        if (!item?.itemId) {
          return;
        }
        void this.router.navigate(buildUpdateListingRouterLink(item));
      }
    });
  }
}
